const memoryCache = new Map();
const CACHE_PREFIX = 'scan-view-fingerprint-v3:';

function clamp(value) {
  return Math.max(0, Math.min(1, value));
}

function loadImage(source) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Could not load reference image: ${source}`));
    image.src = source;
  });
}

function cropRect(image, crop) {
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  if (crop === 'center80') return { sx: width * 0.1, sy: height * 0.1, sw: width * 0.8, sh: height * 0.8 };
  if (crop === 'center60') return { sx: width * 0.2, sy: height * 0.2, sw: width * 0.6, sh: height * 0.6 };
  if (crop === 'top70') return { sx: 0, sy: 0, sw: width, sh: height * 0.7 };
  return { sx: 0, sy: 0, sw: width, sh: height };
}

function vectorCosine(left = [], right = []) {
  if (!left.length || left.length !== right.length) return 0;
  let dot = 0;
  let normLeft = 0;
  let normRight = 0;
  for (let i = 0; i < left.length; i += 1) {
    dot += left[i] * right[i];
    normLeft += left[i] ** 2;
    normRight += right[i] ** 2;
  }
  if (!normLeft || !normRight) return 0;
  return clamp((dot / Math.sqrt(normLeft * normRight) + 1) / 2);
}

function hashSimilarity(left, right) {
  if (!left || !right || left.length !== right.length) return 0;
  let differences = 0;
  for (let i = 0; i < left.length; i += 1) if (left[i] !== right[i]) differences += 1;
  return 1 - differences / left.length;
}

function histogramIntersection(left = [], right = []) {
  if (!left.length || left.length !== right.length) return 0;
  let sum = 0;
  for (let i = 0; i < left.length; i += 1) sum += Math.min(left[i], right[i]);
  return clamp(sum);
}

function normalize(values) {
  const mean = values.reduce((sum, value) => sum + value, 0) / Math.max(1, values.length);
  const variance = values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / Math.max(1, values.length);
  const deviation = Math.sqrt(variance) || 1;
  return values.map((value) => (value - mean) / deviation);
}

function scalarSimilarity(left = 0, right = 0, tolerance = 1) {
  return clamp(1 - Math.abs(left - right) / Math.max(0.0001, tolerance));
}

async function createFingerprint(source, crop = 'full') {
  const image = await loadImage(source);
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  const { sx, sy, sw, sh } = cropRect(image, crop);
  context.drawImage(image, sx, sy, sw, sh, 0, 0, 64, 64);
  const pixels = context.getImageData(0, 0, 64, 64).data;

  const grayscale = new Array(64 * 64);
  const histogram = new Array(64).fill(0);
  let totalLight = 0;
  let totalColorfulness = 0;

  for (let index = 0; index < grayscale.length; index += 1) {
    const offset = index * 4;
    const r = pixels[offset];
    const g = pixels[offset + 1];
    const b = pixels[offset + 2];
    const light = r * 0.299 + g * 0.587 + b * 0.114;
    grayscale[index] = light;
    totalLight += light;
    totalColorfulness += (Math.abs(r - g) + Math.abs(g - b) + Math.abs(b - r)) / 3;
    const bin = Math.min(3, r >> 6) * 16 + Math.min(3, g >> 6) * 4 + Math.min(3, b >> 6);
    histogram[bin] += 1 / grayscale.length;
  }

  const meanLuminance = totalLight / grayscale.length / 255;
  const colorfulness = totalColorfulness / grayscale.length / 255;
  const contrast = Math.sqrt(
    grayscale.reduce((sum, value) => sum + (value / 255 - meanLuminance) ** 2, 0) / grayscale.length,
  );

  const luminance = [];
  const edges = [];
  let absoluteEdgeTotal = 0;
  let absoluteEdgeCount = 0;

  for (let gy = 0; gy < 8; gy += 1) {
    for (let gx = 0; gx < 8; gx += 1) {
      let light = 0;
      let edge = 0;
      for (let y = gy * 8; y < gy * 8 + 8; y += 1) {
        for (let x = gx * 8; x < gx * 8 + 8; x += 1) {
          const value = grayscale[y * 64 + x];
          light += value;
          if (x < 63) {
            const horizontal = Math.abs(value - grayscale[y * 64 + x + 1]);
            edge += horizontal;
            absoluteEdgeTotal += horizontal;
            absoluteEdgeCount += 1;
          }
          if (y < 63) {
            const vertical = Math.abs(value - grayscale[(y + 1) * 64 + x]);
            edge += vertical;
            absoluteEdgeTotal += vertical;
            absoluteEdgeCount += 1;
          }
        }
      }
      luminance.push(light / (64 * 255));
      edges.push(edge / (128 * 255));
    }
  }

  const colorGrid = [];
  for (let gy = 0; gy < 4; gy += 1) {
    for (let gx = 0; gx < 4; gx += 1) {
      let rTotal = 0;
      let gTotal = 0;
      let bTotal = 0;
      let count = 0;
      for (let y = gy * 16; y < gy * 16 + 16; y += 1) {
        for (let x = gx * 16; x < gx * 16 + 16; x += 1) {
          const offset = (y * 64 + x) * 4;
          rTotal += pixels[offset];
          gTotal += pixels[offset + 1];
          bTotal += pixels[offset + 2];
          count += 1;
        }
      }
      const sum = rTotal + gTotal + bTotal || 1;
      colorGrid.push(rTotal / sum, gTotal / sum, bTotal / sum, count / 256);
    }
  }

  let dHash = '';
  let aHash = '';
  const meanLight = luminance.reduce((sum, value) => sum + value, 0) / luminance.length;
  for (let y = 0; y < 8; y += 1) {
    for (let x = 0; x < 8; x += 1) {
      const left = grayscale[(y * 8 + 3) * 64 + (x * 8 + 3)];
      const right = grayscale[(y * 8 + 3) * 64 + Math.min(63, x * 8 + 10)];
      dHash += left > right ? '1' : '0';
      aHash += luminance[y * 8 + x] > meanLight ? '1' : '0';
    }
  }

  return {
    dHash,
    aHash,
    histogram,
    colorGrid: normalize(colorGrid),
    luminance: normalize(luminance),
    edges: normalize(edges),
    meanLuminance,
    contrast,
    edgeDensity: absoluteEdgeTotal / Math.max(1, absoluteEdgeCount) / 255,
    colorfulness,
  };
}

async function cachedFingerprint(path) {
  if (memoryCache.has(path)) return memoryCache.get(path);
  try {
    const stored = localStorage.getItem(`${CACHE_PREFIX}${path}`);
    if (stored) {
      const parsed = JSON.parse(stored);
      memoryCache.set(path, parsed);
      return parsed;
    }
  } catch { /* localStorage can be unavailable in private mode */ }

  const fingerprint = await createFingerprint(path);
  memoryCache.set(path, fingerprint);
  try { localStorage.setItem(`${CACHE_PREFIX}${path}`, JSON.stringify(fingerprint)); } catch { /* ignore quota */ }
  return fingerprint;
}

export function compareFingerprints(query, reference) {
  const globalSimilarity = (
    scalarSimilarity(query.meanLuminance, reference.meanLuminance, 0.42) +
    scalarSimilarity(query.contrast, reference.contrast, 0.24) +
    scalarSimilarity(query.edgeDensity, reference.edgeDensity, 0.18) +
    scalarSimilarity(query.colorfulness, reference.colorfulness, 0.32)
  ) / 4;

  const score =
    hashSimilarity(query.dHash, reference.dHash) * 0.17 +
    hashSimilarity(query.aHash, reference.aHash) * 0.09 +
    vectorCosine(query.luminance, reference.luminance) * 0.18 +
    vectorCosine(query.edges, reference.edges) * 0.18 +
    histogramIntersection(query.histogram, reference.histogram) * 0.12 +
    vectorCosine(query.colorGrid, reference.colorGrid) * 0.16 +
    globalSimilarity * 0.10;

  return clamp(score);
}

function summarizeReferenceScores(rawScores) {
  const sorted = [...rawScores].sort((a, b) => b - a);
  const best = sorted[0] || 0;
  const second = sorted[1] || 0;
  const top = sorted.slice(0, Math.min(3, sorted.length));
  const meanTop = top.reduce((sum, value) => sum + value, 0) / Math.max(1, top.length);
  const consensus = sorted.length === 1
    ? best
    : best * 0.62 + second * 0.28 + meanTop * 0.10;

  return {
    best,
    second,
    meanTop,
    consensus,
    supportCount: sorted.filter((value) => value >= 0.68).length,
    referenceCount: sorted.length,
    topScores: sorted.slice(0, 3),
  };
}

export async function findVisualMatches(imageData, manifest, candidateIds, onProgress) {
  const crops = ['full', 'center80', 'center60', 'top70'];
  const queryFingerprints = [];
  for (let index = 0; index < crops.length; index += 1) {
    queryFingerprints.push(await createFingerprint(imageData, crops[index]));
    onProgress?.((index + 1) / (crops.length + 1), 'Preparing visual signature');
  }

  const allowed = candidateIds?.length ? new Set(candidateIds) : null;
  const targets = manifest.filter((item) => !allowed || allowed.has(item.restaurantId));
  const scores = {};
  let completed = 0;
  const total = Math.max(1, targets.reduce((sum, item) => sum + item.images.length, 0));

  for (const item of targets) {
    const referenceScores = [];
    for (const path of item.images) {
      let bestForReference = 0;
      try {
        const reference = await cachedFingerprint(path);
        for (const query of queryFingerprints) {
          bestForReference = Math.max(bestForReference, compareFingerprints(query, reference));
        }
      } catch (error) {
        console.warn(error);
      }
      referenceScores.push(bestForReference);
      completed += 1;
      onProgress?.(completed / total, 'Comparing local reference photos');
    }
    scores[item.restaurantId] = summarizeReferenceScores(referenceScores);
  }
  return scores;
}
